import { type Accessor, createEffect, createSignal, onCleanup } from "solid-js";

// Clip thumbnails are handed out before the server's queued job has extracted
// them, so their first loads 404 for as long as that job waits in the queue.
// About four minutes in all covers a backlog without polling forever.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000, 60000, 120000];

// Streaming remounts thumbnails on every token. A probe's load event comes
// after the next paint in Firefox, even from the cache, so without this a
// shown image flashes back to its placeholder each time.
const loadedUrls = new Set<string>();

/**
 * Loads `url` off-screen, retrying while it 404s; calls `onLoad` once it has
 * loaded or `onGiveUp` after the last retry. Returns a cancel function.
 */
export function loadPendingImage(
	url: string,
	onLoad: () => void,
	onGiveUp: () => void,
): () => void {
	if (loadedUrls.has(url)) {
		onLoad();
		return () => {};
	}

	let attempt = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;
	let probe: HTMLImageElement | undefined;
	const load = () => {
		// A fresh element each time: re-setting the same src on one can be
		// answered from the failed attempt without asking the server again.
		probe = new Image();
		probe.onload = () => {
			loadedUrls.add(url);
			onLoad();
		};
		probe.onerror = () => {
			const delay = RETRY_DELAYS_MS[attempt++];
			if (delay === undefined) {
				onGiveUp();
				return;
			}
			timer = setTimeout(load, delay);
		};
		probe.src = url;
	};
	load();

	return () => {
		clearTimeout(timer);
		if (probe) {
			probe.onload = null;
			probe.onerror = null;
		}
	};
}

export type PendingImage = {
	/** The URL once it has loaded; undefined while pending or after giving up. */
	src: Accessor<string | undefined>;
	/** True once every retry has failed. */
	failed: Accessor<boolean>;
};

/** loadPendingImage for a reactive URL, exposing it once it has loaded. */
export function usePendingImage(
	url: Accessor<string | null | undefined>,
): PendingImage {
	const [src, setSrc] = createSignal<string>();
	const [failed, setFailed] = createSignal(false);

	createEffect(() => {
		const current = url();
		setSrc(undefined);
		setFailed(false);
		if (!current) {
			return;
		}
		onCleanup(
			loadPendingImage(
				current,
				() => setSrc(current),
				() => setFailed(true),
			),
		);
	});

	return { src, failed };
}
